import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContentMaxPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import {
  Phone, Mail, MapPin, Users, Search, ChevronDown, ChevronRight,
  Loader2, Clock, Shield, Star, Building2, Globe, Link2, AlertTriangle,
  Briefcase, Hash, PhoneOff, Copy, ExternalLink, User, Skull
} from 'lucide-react';
import { toast } from 'sonner';
import { ClickToCall } from '@/components/common/ClickToCall';
import { useAssertivaConsulta } from '@/hooks/useAssertivaConsulta';
import { useAssertivaContatos, type AssertivaContato, type AssertivaSocio } from '@/hooks/useAssertivaContatos';
import { formatarTelefone } from '@/utils/formatters';
import { PhoneWithOperator } from '@/components/ui/phone-with-operator';
import { format } from 'date-fns';

interface AssertivaDadosCardProps {
  cnpj?: string | null;
  cpf?: string | null;
  email?: string | null;
  telefone?: string | null;
  nomeProprietario?: string | null;
  razaoSocial?: string | null;
  leadId?: string | null;
}

function ScoreBadge({ score }: { score: number | null }) {
  if (score == null) return null;
  const variant = score >= 70 ? 'default' : score >= 40 ? 'secondary' : 'destructive';
  return (
    <Badge variant={variant} className="text-[10px] px-1.5 py-0">
      {score}%
    </Badge>
  );
}

function OrigemBadge({ cnpj, cpf }: { cnpj?: string | null; cpf?: string | null }) {
  if (cnpj) return <Badge variant="outline" className="text-[9px] px-1 py-0 border-info text-info">PJ</Badge>;
  if (cpf) return <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary text-primary">PF</Badge>;
  return null;
}

export function AssertivaDadosCard({ cnpj, cpf, email, telefone, nomeProprietario, razaoSocial, leadId }: AssertivaDadosCardProps) {
  const [open, setOpen] = useState(false);
  const [cnaesOpen, setCnaesOpen] = useState(false);
  const [participacoesOpen, setParticipacoesOpen] = useState(false);
  const [consultaIds, setConsultaIds] = useState<string[]>([]);

  // Tipo de consulta selecionado - TODOS os 5 tipos sempre visíveis
  const tiposDisponiveis = [
    { value: 'cnpj', label: 'CNPJ', chave: cnpj || '' },
    { value: 'cpf', label: 'CPF', chave: cpf || '' },
    { value: 'email', label: 'Email', chave: email || '' },
    { value: 'telefone', label: 'Telefone', chave: telefone || '' },
    { value: 'nome', label: 'Nome', chave: nomeProprietario || '' },
  ];
  const [tipoConsulta, setTipoConsulta] = useState(cnpj ? 'cnpj' : tiposDisponiveis.find(t => t.chave)?.value || 'cnpj');
  const [chaveManual, setChaveManual] = useState('');

  // Quando muda o tipo, preenche com valor do lead (se tiver)
  useEffect(() => {
    const selected = tiposDisponiveis.find(t => t.value === tipoConsulta);
    setChaveManual(selected?.chave || '');
  }, [tipoConsulta]);

  const { consultar, isLoading: consultando } = useAssertivaConsulta();
  const {
    telefones, emails, enderecos, socios, isLoading, ultimaConsulta,
    dadosCadastrais, todosDadosCadastrais, cnaesSecundarias, redesSociais, participacoes, refetch,
  } = useAssertivaContatos(cnpj, cpf, consultaIds.length > 0 ? consultaIds : null, email, telefone, nomeProprietario, leadId);

  const handleEnriquecer = async () => {
    if (!chaveManual.trim()) return;
    const result = await consultar(tipoConsulta as any, chaveManual.trim(), { lead_id: leadId || undefined });
    if (result?.sucesso) {
      if (result.consulta_id) {
        setConsultaIds(prev => prev.includes(result.consulta_id!) ? prev : [...prev, result.consulta_id!]);
      }
      setTimeout(() => refetch(), 500);
    }
  };

  const temDados = telefones.length > 0 || emails.length > 0 || enderecos.length > 0 || socios.length > 0 || todosDadosCadastrais.length > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="border-dashed border-accent/50">
        <CollapsibleTrigger asChild>
          <CardHeader className="py-2 px-3 cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <Shield className="w-4 h-4 text-accent-foreground" />
                <CardTitle className="text-sm font-medium">Enriquecimento Assertiva</CardTitle>
                {temDados && (
                  <Badge variant="outline" className="text-[10px]">
                    {telefones.length + emails.length} contatos
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {ultimaConsulta && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {format(new Date(ultimaConsulta), 'dd/MM/yy HH:mm')}
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="px-3 pb-3 pt-0">
            {/* Seletor de tipo + botão Enriquecer */}
            <div className="flex items-center gap-2 mb-3">
              <Select value={tipoConsulta} onValueChange={setTipoConsulta}>
                <SelectTrigger className="h-7 text-xs w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContentMaxPriority>
                  {tiposDisponiveis.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-xs">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContentMaxPriority>
              </Select>
              <Input
                value={chaveManual}
                onChange={(e) => setChaveManual(e.target.value)}
                placeholder={`Digite o ${tiposDisponiveis.find(t => t.value === tipoConsulta)?.label || 'valor'}...`}
                className="h-7 text-xs flex-1"
                onKeyDown={(e) => { if (e.key === 'Enter') handleEnriquecer(); }}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={consultando || !chaveManual.trim()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEnriquecer();
                }}
              >
                {consultando ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                Enriquecer
              </Button>
            </div>

            {isLoading ? (
              <div className="text-sm text-muted-foreground py-4 text-center">Carregando...</div>
            ) : !temDados ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                Nenhum dado enriquecido. Selecione o tipo e clique em "Enriquecer".
              </div>
            ) : (
              <div className="space-y-3">
                {/* Dados agrupados por pessoa (consulta_id) */}
                {(() => {
                  const cadastralConsultaIds = new Set(todosDadosCadastrais.map(c => c.consulta_id));
                  const telefonesOrfaos = telefones.filter(t => !cadastralConsultaIds.has(t.consulta_id));
                  const emailsOrfaos = emails.filter(e => !cadastralConsultaIds.has(e.consulta_id));
                  const enderecosOrfaos = enderecos.filter(e => !cadastralConsultaIds.has(e.consulta_id));

                  return (
                    <>
                      {todosDadosCadastrais.map((cadastral) => {
                        const dc = cadastral.dados_extra as any;
                        if (!dc) return null;
                        const telsPessoa = telefones.filter(t => t.consulta_id === cadastral.consulta_id);
                        const emailsPessoa = emails.filter(e => e.consulta_id === cadastral.consulta_id);
                        const endPessoa = enderecos.filter(e => e.consulta_id === cadastral.consulta_id);
                        const nomePessoa = dc.nome || dc.razaoSocial || dc.nomeFantasia || '';

                        return (
                          <div key={cadastral.id} className="border border-border/50 rounded-md p-2 space-y-2">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-xs font-medium text-muted-foreground">Dados Cadastrais</span>
                              <OrigemBadge cnpj={cadastral.cnpj} cpf={cadastral.cpf} />
                              {nomePessoa && <span className="text-xs font-semibold truncate">{nomePessoa}</span>}
                            </div>
                            <div className="bg-muted/30 rounded px-2 py-1.5 space-y-1">
                              {dc.razaoSocial && (
                                <div className="text-xs"><span className="text-muted-foreground">Razão Social:</span> <span className="font-medium">{dc.razaoSocial}</span></div>
                              )}
                              {dc.nomeFantasia && (
                                <div className="text-xs"><span className="text-muted-foreground">Fantasia:</span> <span className="font-medium">{dc.nomeFantasia}</span></div>
                              )}
                              {dc.cnae && (
                                <div className="text-xs"><span className="text-muted-foreground">CNAE:</span> {dc.cnae} — {dc.cnaeDescricao || ''}</div>
                              )}
                              {dc.cnaeGrupo && (
                                <div className="text-[10px] text-muted-foreground ml-2">Grupo: {dc.cnaeGrupo}</div>
                              )}
                              {dc.cnaeSubgrupo && (
                                <div className="text-[10px] text-muted-foreground ml-2">Subgrupo: {dc.cnaeSubgrupo}</div>
                              )}
                              {dc.nome && !dc.razaoSocial && (
                                <div className="text-xs"><span className="text-muted-foreground">Nome:</span> <span className="font-medium">{dc.nome}</span></div>
                              )}
                              {dc.cpfFormatado && (
                                <div className="text-xs flex items-center gap-1">
                                  <span className="text-muted-foreground">CPF:</span>
                                  <button
                                    onClick={(ev) => { ev.stopPropagation(); navigator.clipboard.writeText(dc.cpfFormatado); toast.success('CPF copiado!'); }}
                                    className="font-mono inline-flex items-center gap-0.5 hover:text-primary cursor-pointer"
                                    title="Clique para copiar"
                                  >
                                    {dc.cpfFormatado} <Copy className="w-2.5 h-2.5" />
                                  </button>
                                </div>
                              )}
                              {dc.rg && (
                                <div className="text-[10px]"><span className="text-muted-foreground">RG:</span> {dc.rg}{dc.ufRg ? ` (${dc.ufRg})` : ''}</div>
                              )}
                              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                                {dc.porteEmpresa && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Porte:</span> {dc.porteEmpresa}</div>
                                )}
                                {dc.naturezaJuridica && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Nat. Jurídica:</span> {dc.naturezaJuridica}</div>
                                )}
                                {dc.situacaoCadastral && (
                                  <div className="text-[10px]">
                                    <span className="text-muted-foreground">Situação:</span>{' '}
                                    <Badge variant={dc.situacaoCadastral === 'ATIVA' || dc.situacaoCadastral === 'REGULAR' ? 'default' : 'destructive'} className="text-[9px] px-1 py-0">
                                      {dc.situacaoCadastral}
                                    </Badge>
                                    {dc.dataSituacaoCadastral && <span className="text-[10px] text-muted-foreground ml-1">em {dc.dataSituacaoCadastral}</span>}
                                  </div>
                                )}
                                {dc.dataAbertura && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Abertura:</span> {dc.dataAbertura}</div>
                                )}
                                {dc.idadeEmpresa && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Idade:</span> {dc.idadeEmpresa}</div>
                                )}
                                {dc.quantidadeFuncionarios != null && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Funcionários:</span> {dc.quantidadeFuncionarios}</div>
                                )}
                                {dc.capitalSocial && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Capital:</span> {dc.capitalSocial}</div>
                                )}
                                {dc.temGoogleMeuNegocio && (
                                  <div className="text-[10px]">
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 border-success text-success">Google Meu Negócio</Badge>
                                  </div>
                                )}
                                {dc.dataNascimento && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Nascimento:</span> {dc.dataNascimento}</div>
                                )}
                                {dc.idade && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Idade:</span> {dc.idade} anos</div>
                                )}
                                {dc.sexo && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Sexo:</span> {dc.sexo}</div>
                                )}
                                {dc.signo && (
                                  <div className="text-[10px]"><span className="text-muted-foreground">Signo:</span> {dc.signo}</div>
                                )}
                              </div>
                              {dc.maeNome && (
                                <div className="text-[10px]"><span className="text-muted-foreground">Mãe:</span> {dc.maeNome}</div>
                              )}
                              {dc.obitoProvavel && (
                                <div className="text-[10px] flex items-center gap-1 text-destructive font-medium">
                                  <Skull className="w-3 h-3" /> Óbito provável
                                </div>
                              )}
                              {dc.ppe && (
                                <div className="text-[10px]">
                                  <Badge variant="warning" className="text-[9px] px-1 py-0">
                                    <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />PPE
                                  </Badge>
                                  {dc.ppeObservacao && <span className="text-muted-foreground ml-1">{dc.ppeObservacao}</span>}
                                </div>
                              )}
                              {dc.site && (
                                <div className="text-[10px]">
                                  <span className="text-muted-foreground">Site:</span>{' '}
                                  <a href={dc.site.startsWith('http') ? dc.site : `https://${dc.site}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                    {dc.site}
                                  </a>
                                </div>
                              )}
                            </div>

                            {/* Telefones DESTA pessoa */}
                            {telsPessoa.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                                  <span className="text-xs font-medium text-muted-foreground">Telefones ({telsPessoa.length})</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                                  {telsPessoa.map((t) => (
                                    <div key={t.id} className="bg-muted/30 rounded px-2 py-1">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <ClickToCall telefone={t.valor} className="text-primary hover:text-primary/80">
                                            <Phone className="w-3 h-3" />
                                          </ClickToCall>
                                          <span className="text-xs font-mono">{formatarTelefone(t.valor)}</span>
                                          <PhoneWithOperator phone={t.valor} variant="badge-only" />
                                          {t.classificacao === 'whatsapp' && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary text-primary">WA</Badge>
                                          )}
                                          {t.dados_extra?.whatsapp && t.classificacao !== 'whatsapp' && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary text-primary">WA</Badge>
                                          )}
                                          {t.tipo === 'celular' && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0">Cel</Badge>
                                          )}
                                          {t.dados_extra?.relacao && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0">{t.dados_extra.relacao}</Badge>
                                          )}
                                          {t.dados_extra?.naoPerturbe && (
                                            <Badge variant="destructive" className="text-[9px] px-1 py-0">
                                              <PhoneOff className="w-2.5 h-2.5 mr-0.5" />NP
                                            </Badge>
                                          )}
                                          {t.dados_extra?.hotphone && (
                                            <Badge className="text-[9px] px-1 py-0 bg-warning hover:bg-warning text-white">HOT</Badge>
                                          )}
                                        </div>
                                        <ScoreBadge score={t.score_confianca} />
                                      </div>
                                      {t.dados_extra?.ultimoContato && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{t.dados_extra.ultimoContato}</div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Emails DESTA pessoa */}
                            {emailsPessoa.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                                  <span className="text-xs font-medium text-muted-foreground">Emails ({emailsPessoa.length})</span>
                                </div>
                                <div className="space-y-1">
                                  {emailsPessoa.map((e) => (
                                    <div key={e.id} className="flex items-center justify-between bg-muted/30 rounded px-2 py-1">
                                      <div className="flex items-center gap-1.5 truncate max-w-[80%]">
                                        <a href={`mailto:${e.valor}`} className="text-xs text-primary hover:underline truncate">
                                          {e.valor}
                                        </a>
                                      </div>
                                      <ScoreBadge score={e.score_confianca} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Endereços DESTA pessoa */}
                            {endPessoa.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                                  <span className="text-xs font-medium text-muted-foreground">Endereços ({endPessoa.length})</span>
                                </div>
                                <div className="space-y-1">
                                  {endPessoa.map((e) => (
                                    <div key={e.id} className="text-xs bg-muted/30 rounded px-2 py-1">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1">
                                          <span>{e.dados_extra?.tipoLogradouro ? `${e.dados_extra.tipoLogradouro} ` : ''}{e.valor}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          {e.dados_extra?.precisaoCep && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0">{e.dados_extra.precisaoCep}</Badge>
                                          )}
                                          {e.classificacao === 'principal' && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0">Principal</Badge>
                                          )}
                                        </div>
                                      </div>
                                      {e.dados_extra?.latitude && e.dados_extra?.longitude && (
                                        <button
                                          onClick={(ev) => {
                                            ev.stopPropagation();
                                            window.open(`https://www.google.com/maps?q=${e.dados_extra.latitude},${e.dados_extra.longitude}`, '_blank');
                                          }}
                                          className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5 cursor-pointer"
                                        >
                                          <ExternalLink className="w-2.5 h-2.5" />
                                          Ver no Google Maps
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Telefones órfãos */}
                      {telefonesOrfaos.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Phone className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-xs font-medium text-muted-foreground">Outros Telefones ({telefonesOrfaos.length})</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            {telefonesOrfaos.map((t) => (
                              <div key={t.id} className="bg-muted/30 rounded px-2 py-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <OrigemBadge cnpj={t.cnpj} cpf={t.cpf} />
                                    <ClickToCall telefone={t.valor} className="text-primary hover:text-primary/80">
                                      <Phone className="w-3 h-3" />
                                    </ClickToCall>
                                    <span className="text-xs font-mono">{formatarTelefone(t.valor)}</span>
                                    <PhoneWithOperator phone={t.valor} variant="badge-only" />
                                    {t.classificacao === 'whatsapp' && (
                                      <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary text-primary">WA</Badge>
                                    )}
                                    {t.dados_extra?.whatsapp && t.classificacao !== 'whatsapp' && (
                                      <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary text-primary">WA</Badge>
                                    )}
                                    {t.tipo === 'celular' && (
                                      <Badge variant="outline" className="text-[9px] px-1 py-0">Cel</Badge>
                                    )}
                                    {t.dados_extra?.relacao && (
                                      <Badge variant="outline" className="text-[9px] px-1 py-0">{t.dados_extra.relacao}</Badge>
                                    )}
                                    {t.dados_extra?.naoPerturbe && (
                                      <Badge variant="destructive" className="text-[9px] px-1 py-0">
                                        <PhoneOff className="w-2.5 h-2.5 mr-0.5" />NP
                                      </Badge>
                                    )}
                                    {t.dados_extra?.hotphone && (
                                      <Badge className="text-[9px] px-1 py-0 bg-warning hover:bg-warning text-white">HOT</Badge>
                                    )}
                                  </div>
                                  <ScoreBadge score={t.score_confianca} />
                                </div>
                                {t.dados_extra?.ultimoContato && (
                                  <div className="text-[10px] text-muted-foreground mt-0.5">{t.dados_extra.ultimoContato}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Emails órfãos */}
                      {emailsOrfaos.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-xs font-medium text-muted-foreground">Outros Emails ({emailsOrfaos.length})</span>
                          </div>
                          <div className="space-y-1">
                            {emailsOrfaos.map((e) => (
                              <div key={e.id} className="flex items-center justify-between bg-muted/30 rounded px-2 py-1">
                                <div className="flex items-center gap-1.5 truncate max-w-[80%]">
                                  <OrigemBadge cnpj={e.cnpj} cpf={e.cpf} />
                                  <a href={`mailto:${e.valor}`} className="text-xs text-primary hover:underline truncate">
                                    {e.valor}
                                  </a>
                                </div>
                                <ScoreBadge score={e.score_confianca} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Endereços órfãos */}
                      {enderecosOrfaos.length > 0 && (
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-xs font-medium text-muted-foreground">Outros Endereços ({enderecosOrfaos.length})</span>
                          </div>
                          <div className="space-y-1">
                            {enderecosOrfaos.map((e) => (
                              <div key={e.id} className="text-xs bg-muted/30 rounded px-2 py-1">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-1">
                                    <OrigemBadge cnpj={e.cnpj} cpf={e.cpf} />
                                    <span>{e.dados_extra?.tipoLogradouro ? `${e.dados_extra.tipoLogradouro} ` : ''}{e.valor}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {e.dados_extra?.precisaoCep && (
                                      <Badge variant="outline" className="text-[9px] px-1 py-0">{e.dados_extra.precisaoCep}</Badge>
                                    )}
                                    {e.classificacao === 'principal' && (
                                      <Badge variant="outline" className="text-[9px] px-1 py-0">Principal</Badge>
                                    )}
                                  </div>
                                </div>
                                {e.dados_extra?.latitude && e.dados_extra?.longitude && (
                                  <button
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      window.open(`https://www.google.com/maps?q=${e.dados_extra.latitude},${e.dados_extra.longitude}`, '_blank');
                                    }}
                                    className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5 cursor-pointer"
                                  >
                                    <ExternalLink className="w-2.5 h-2.5" />
                                    Ver no Google Maps
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* 5. Redes Sociais */}
                {redesSociais.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">Redes Sociais ({redesSociais.length})</span>
                    </div>
                    <div className="space-y-1">
                      {redesSociais.map((r) => (
                        <div key={r.id} className="flex items-center gap-1.5 bg-muted/30 rounded px-2 py-1">
                          <Link2 className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                          {r.classificacao && <Badge variant="outline" className="text-[9px] px-1 py-0">{r.classificacao}</Badge>}
                          <a href={r.valor.startsWith('http') ? r.valor : `https://${r.valor}`} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline truncate">
                            {r.valor}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 6. Sócios */}
                {socios.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Users className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground">Sócios ({socios.length})</span>
                      </div>
                      <div className="space-y-1">
                        {socios.map((s) => (
                          <div key={s.id} className="flex items-center justify-between bg-muted/30 rounded px-2 py-1.5">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs font-medium truncate">{s.nome || '—'}</span>
                                {s.decisor && (
                                  <Badge className="text-[9px] px-1 py-0 bg-accent text-accent-foreground hover:bg-accent/80">
                                    <Star className="w-2.5 h-2.5 mr-0.5" />
                                    DECISOR
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                {s.qualificacao && <span>{s.qualificacao}</span>}
                                {s.participacao != null && <span>{s.participacao}%</span>}
                                {s.cpf_cnpj_socio && (
                                  <button
                                    onClick={(ev) => {
                                      ev.stopPropagation();
                                      navigator.clipboard.writeText(s.cpf_cnpj_socio!);
                                      toast.success('CPF copiado!');
                                    }}
                                    className="font-mono inline-flex items-center gap-0.5 hover:text-primary cursor-pointer"
                                    title="Clique para copiar"
                                  >
                                    {s.cpf_cnpj_socio}
                                    <Copy className="w-2.5 h-2.5" />
                                  </button>
                                )}
                                {s.data_entrada && <span>Entrada: {new Date(s.data_entrada + 'T00:00:00').toLocaleDateString('pt-BR')}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* 7. CNAEs Secundárias (colapsável) */}
                {cnaesSecundarias?.dados_extra?.cnaes?.length > 0 && (
                  <Collapsible open={cnaesOpen} onOpenChange={setCnaesOpen}>
                    <CollapsibleTrigger className="flex items-center gap-1.5 cursor-pointer hover:bg-muted/30 rounded px-1 py-0.5 w-full">
                      {cnaesOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      <Hash className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        CNAEs Secundárias ({cnaesSecundarias.dados_extra.cnaes.length})
                      </span>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-0.5 mt-1 ml-5">
                        {cnaesSecundarias.dados_extra.cnaes.map((c: any, i: number) => (
                          <div key={i} className="text-[10px] bg-muted/30 rounded px-2 py-0.5">
                            <span className="font-mono text-muted-foreground">{c.cnae || c.codigo}</span>
                            {' — '}
                            <span>{c.descricao}</span>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {/* 8. Participações em Empresas (colapsável) */}
                {participacoes.length > 0 && (
                  <Collapsible open={participacoesOpen} onOpenChange={setParticipacoesOpen}>
                    <CollapsibleTrigger className="flex items-center gap-1.5 cursor-pointer hover:bg-muted/30 rounded px-1 py-0.5 w-full">
                      {participacoesOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground">
                        Participações em Empresas ({participacoes.length})
                      </span>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="space-y-1 mt-1 ml-5">
                        {participacoes.map((p) => (
                          <div key={p.id} className="text-[10px] bg-muted/30 rounded px-2 py-1">
                            <div className="font-medium">{p.valor}</div>
                            <div className="text-muted-foreground">
                              {p.dados_extra?.qualificacao && <span>{p.dados_extra.qualificacao}</span>}
                              {p.dados_extra?.participacao && <span> · {p.dados_extra.participacao}%</span>}
                              {p.dados_extra?.situacao && <span> · {p.dados_extra.situacao}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
